import { heapEvent } from "@equipmentshare/heaper";
import * as Ant from "antd";
import { identity, Option, pipe, ReadonlyArray } from "fp";
import React from "react";
import { useRouteMatch } from "react-router-dom";
import styled from "styled-components";
import { reducer, type, useDispatch } from "~/Action";
import * as Animation from "~/Animation";
import * as Color from "~/Color";
import { useContext } from "~/Context";
import { Dashboard } from "~/Dashboard";
import * as Document from "~/Document";
import * as Driver from "~/Driver";
import { DVIR, DVIRs } from "~/DVIR";
import * as ELD from "~/ELD";
import * as Icon from "~/Icon";
import { ID } from "~/ID";
import * as Layout from "~/Layout";
import * as Log from "~/Log";
import { Menu } from "~/Menu";
import * as Messaging from "~/Messaging";
import { Settings } from "~/Settings";
import * as Shadow from "~/Shadow";
import { useSelector } from "~/State";
import { Time } from "~/Time";
import { UniversalHeader } from "~/UniversalHeader";

export function MainMenu() {
  const {
    identity: { userId },
  } = useContext();
  userId && Time.Zone.Default.useSet(ID.from(userId));

  const selected = MainMenu.useSelected();
  const settings = Settings.use();
  const logsClass = selected.includes("Logs") ? "ant-menu-item-selected" : "";
  const dvirsClass = selected.includes("DVIRs") ? "ant-menu-item-selected" : "";

  return (
    <MainMenu.Container>
      <MainMenu.Toggle />
      <Menu
        style={{ width: MainMenu.useWidth() }}
        selectedKeys={
          MainMenu.useSelected() as React.ComponentProps<
            typeof Menu
          >["selectedKeys"]
        }
      >
        <FleetMenuItem />
        <DriversMenuItem />
        <LogsSubmenu className={logsClass} />
        <DVIRsSubmenu className={dvirsClass} />
        <DocumentsMenuItem />
        <MessagesMenuItem />
      </Menu>
      <Menu selectable={false} style={{ width: MainMenu.useWidth() }}>
        <SettingsMenuItem settings={settings} />
        <Menu.Item
          key="Help"
          icon={<Icon.QuestionCircle />}
          onClick={() =>
            window.open(
              "https://intercom.help/equipmentshare/en/collections/1717922-e-logs-app-help"
            )
          }
        >
          Help
        </Menu.Item>
      </Menu>
      <Settings {...settings} />
    </MainMenu.Container>
  );
}

export const FleetMenuItem = ({
  ...props
}: React.ComponentProps<typeof Ant.Menu.Item>) => {
  const fleetNavigation = Dashboard.Route.use();
  return (
    <Menu.Item
      {...props}
      key="Fleet"
      icon={<Icon.MapMarkedAlt />}
      onClick={() => {
        heapEvent("E-Logs - Navigation Menu - Click - Fleet", {});
        fleetNavigation.go();
      }}
    >
      Fleet
    </Menu.Item>
  );
};

export const DriversMenuItem = ({
  ...props
}: React.ComponentProps<typeof Ant.Menu.Item>): JSX.Element => {
  const driversNavigation = Driver.Overview.Route.use();
  return (
    <Menu.Item
      {...props}
      key="Drivers"
      icon={<Icon.Users />}
      onClick={() => {
        heapEvent("E-Logs - Navigation Menu - Click - Drivers", {});
        driversNavigation.go();
      }}
    >
      Drivers
    </Menu.Item>
  );
};

export const LogsSubmenu = ({
  ...props
}: React.ComponentProps<
  typeof Ant.Menu.Item & {
    readonly className: string;
  }
>): JSX.Element => {
  const logNavigation = Log.Summaries.Page.Route.use();
  return (
    <Menu.SubMenu
      key="Logs"
      {...props}
      onTitleClick={() => {
        heapEvent("E-Logs - Navigation Menu - Click - Logs", {});
        logNavigation.go();
      }}
      className={props.className}
      title={<Menu.Item.Title>Logs</Menu.Item.Title>}
      icon={<Icon.Truck />}
    >
      <Menu.Item
        style={{ margin: 0 }}
        key="Unassigned"
        onClick={Log.Unassigned.Route.useDefault().go}
        icon={
          <Icon.FixedWidth>
            <Icon.UserSlash />
          </Icon.FixedWidth>
        }
      >
        Unassigned
      </Menu.Item>
      <Menu.Item
        style={{ margin: 0 }}
        key="Submissions"
        onClick={ELD.Route.use().go}
        icon={
          <Icon.FixedWidth>
            <Icon.PaperPlane />
          </Icon.FixedWidth>
        }
      >
        Submissions
      </Menu.Item>
    </Menu.SubMenu>
  );
};

export const DVIRsSubmenu = ({
  ...props
}: React.ComponentProps<
  typeof Ant.Menu.Item & {
    readonly className: string;
  }
>): JSX.Element => {
  const dvirNavigation = DVIRs.Route.WithoutInterval.use();
  return (
    <Menu.SubMenu
      {...props}
      key="DVIRs"
      onTitleClick={() => {
        heapEvent("E-Logs - Navigation Menu - Click - DVIRs", {});
        dvirNavigation.go();
      }}
      className={props.className}
      title={<Menu.Item.Title>DVIRs</Menu.Item.Title>}
      icon={<Icon.FileContract style={{ marginBottom: "-2px" }} />}
    >
      <Menu.Item
        style={{ margin: 0 }}
        key="Missing"
        onClick={DVIR.Missing.Route.use().go}
        icon={<Icon.FileTimes style={{ marginBottom: "-2px" }} />}
      >
        Logs without DVIRs
      </Menu.Item>
    </Menu.SubMenu>
  );
};

export const DocumentsMenuItem = ({
  ...props
}: React.ComponentProps<typeof Ant.Menu.Item>): JSX.Element => {
  const documentsNavigation = Document.Route.use();
  return (
    <Menu.Item
      {...props}
      key="Documents"
      icon={<Icon.PaperClip />}
      onClick={() => {
        heapEvent("E-Logs - Navigation Menu - Click - Documents", {});
        documentsNavigation.go();
      }}
    >
      Documents
    </Menu.Item>
  );
};

export const MessagesMenuItem = ({
  ...props
}: React.ComponentProps<typeof Ant.Menu.Item>): JSX.Element => {
  const messagesNavigation = Messaging.Route.use();
  return (
    <Menu.Item
      {...props}
      key="Messages"
      icon={<Icon.CommentDots />}
      onClick={() => {
        heapEvent("E-Logs - Navigation Menu - Click - Messages", {});
        messagesNavigation.go();
      }}
    >
      Messages
    </Menu.Item>
  );
};

export const SettingsMenuItem = ({
  ...props
}: React.ComponentProps<typeof Ant.Menu.Item> & {
  readonly settings: Settings;
}): JSX.Element => {
  return (
    <Menu.Item
      {...props}
      key="Settings"
      icon={<Icon.Cog />}
      onClick={() => {
        heapEvent("E-Logs - Navigation Menu - Click - Settings", {});
        props.settings.show();
      }}
    >
      Settings
    </Menu.Item>
  );
};

export namespace MainMenu {
  export const animationEase = () => Animation.ease();
  export const animationSpeed = () => Animation.medium();
  export const animationStyles = () =>
    Animation.Transition.styles(animationSpeed());

  export const widthOpenPixels = () => 155;
  export const widthClosedPixels = () => 48;

  export const useWidth = () =>
    `${useIsOpen() ? widthOpenPixels() : widthClosedPixels()}px`;

  export const useIsOpen = () =>
    useSelector(({ mainMenu: { isOpen } }) => isOpen);

  export const useSelected = (): readonly string[] =>
    pipe(
      [
        [Driver.Overview.Route.definition(), ["Drivers"]],
        [Log.Summaries.Page.Route.definition(), ["Logs"]],
        [ELD.Route.definition(), ["Logs", "Submissions"]],
        [Log.Unassigned.Route.definition(), ["Logs", "Unassigned"]],
        [DVIR.Missing.Route.definition(), ["DVIRs", "Missing"]],
        [DVIRs.Route.WithoutInterval.definition(), ["DVIRs"]],
        [Document.Route.definition(), ["Documents"]],
        [Messaging.Route.definition(), ["Messages"]],
        [Dashboard.Route.definition(), ["Fleet"]],
      ] as const,
      ReadonlyArray.map(([definition, selected]) =>
        pipe(
          Option.fromNullable(useRouteMatch(definition)),
          Option.map(() => selected)
        )
      ),
      ReadonlyArray.findFirstMap(identity),
      Option.getOrElseW(() => [])
    );

  export type State = { readonly isOpen: boolean };
  export namespace State {
    export const initial = (): State => ({ isOpen: true });
  }

  export namespace Action {
    export const { TYPE, create, reduce } = pipe(
      type("Main menu toggled" as const),
      reducer(({ state }) => (state.mainMenu.isOpen = !state.mainMenu.isOpen))
    );
  }

  export const Container = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    position: absolute;
    z-index: 100;
    top: ${() => UniversalHeader.height()};
    bottom: 0;

    width: ${() => useWidth()};
    background: white;
    ${() => Shadow.Box.styles(Shadow.large())}
    ${animationStyles}

    & .ant-menu-vertical {
      border-right: none;
      ${animationStyles}

      &:first-of-type {
        flex: 1;
      }
    }

    .ant-menu-item,
    .ant-menu-submenu-title {
      text-overflow: unset;
      height: 40px;
      margin: 0 !important;

      & > svg {
        position: absolute;
        left: ${() => widthClosedPixels() / 2}px;
        top: 50%;
        transform: translate(-50%, -50%);
        ${animationStyles}
      }

      & > span {
        position: absolute;
        left: ${() => (useIsOpen() ? "2.8em" : "4em")};
        ${animationStyles}
      }

      & > .ant-menu-submenu-arrow {
        position: absolute;
        ${() => (useIsOpen() ? "" : "right: -2em;")}
        ${animationStyles}
      }
    }
  `;

  export function Toggle() {
    const dispatch = useDispatch();
    return (
      <Toggle.Container onClick={() => dispatch(MainMenu.Action.create())}>
        <Toggle.Hide>
          <Layout.Inline.Component right>
            <Icon.ChevronLeft />
          </Layout.Inline.Component>
          Hide
        </Toggle.Hide>
        <Toggle.Show>
          <Icon.ChevronRight />
        </Toggle.Show>
      </Toggle.Container>
    );
  }

  export namespace Toggle {
    const maxOpacity = () => 0.3 as const;

    export const Container = styled.div`
      overflow: hidden;
      position: relative;
      height: 1.5em;
      width: 100%;
      background: ${() => Color.almostWhite()};

      & > div {
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        display: flex;
        align-items: center;
        white-space: nowrap;
        font-size: 12px;
        ${animationStyles}
      }
    `;

    export const Show = styled.div`
      justify-content: center;
      width: ${() => widthClosedPixels()}px;
      opacity: ${() => (useIsOpen() ? 0 : maxOpacity())};
    `;

    export const Hide = styled.div`
      justify-content: flex-end;
      width: calc(${() => widthOpenPixels()}px - ${Layout.Block.padding()});
      opacity: ${() => (useIsOpen() ? maxOpacity() : 0)};
    `;
  }
}
